import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { format, parseISO, isSameDay } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar, Clock, Check, X, Loader2, AlertCircle } from 'lucide-react';
import { Lock } from 'lucide-react';
import { Card } from '@mui/material';
import axios from 'axios';
import '../../Final_Css/positiveflowbuttons.css';
import './SlotBookingPage.css'; // Add this import

const PRIMARY = '#0191c8';
const SUCCESS = '#10b981';
const SUCCESS_LIGHT = '#ecfdf5';
const SUCCESS_BORDER = '#a7f3d0';
const ERROR = '#ef4444';
const ERROR_LIGHT = '#fef2f2';
const ERROR_BORDER = '#fecaca';

export default function SlotBookingPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Get employee and assignId from previous page
  const employeeId = location.state?.employeeId;
  const employeeName = location.state?.employeeName;
  const assignId = location.state?.assignId;
  const mode = location.state?.mode || 'add';
  const prefillDate = location.state?.prefillDate;
  const prefillTime = location.state?.prefillTime;
  const examEmployeeId = location.state?.examEmployeeId;
  const examId = location.state?.examId;
  const employeeGuid = location.state?.employeeGuid;

  const employee = employeeName ? {
    id: employeeId,
    name: employeeName,
  } : null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availabilityData, setAvailabilityData] = useState(null);
  const [assignmentData, setAssignmentData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isBooking, setIsBooking] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [bookingError, setBookingError] = useState(null);
  const [examIdFromDetails, setExamIdFromDetails] = useState(null);

  // Clear error after 4 seconds
  useEffect(() => {
    if (bookingError) {
      const timer = setTimeout(() => {
        setBookingError(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [bookingError]);

  // Fetch exam ID from assignment details endpoint
  useEffect(() => {
    const fetchExamId = async () => {
      if (!assignId) return;

      try {
        const response = await axios.get(`/coordinators/assignment-details/?assigned_id=${assignId}`, {
          withCredentials: true
        });
        const data = response.data;
        console.log('Assessment ID fetch response:', data);

        if (data.assigned?.ExamID) {
          setExamIdFromDetails(data.assigned.ExamID);
        }
      } catch (error) {
        console.error('Error fetching assessment ID:', error);
      }
    };

    fetchExamId();
  }, [assignId]);

  // Fetch availability data
  useEffect(() => {
    const fetchAvailability = async () => {
      if (!assignId) {
        setError('No assignment ID provided');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`/coordinators/assignment_slot_availability/?assignedId=${assignId}`, {
          withCredentials: true
        });
        const data = response.data;
        if (!data.success) {
          throw new Error(data.message || 'Failed to load data');
        }

        setAvailabilityData(data.data.availability);
        setAssignmentData(data.data.assignment);

        const slots = data.data.availability.possible_slots.map(slot => ({
          ...slot,
          slot_start: parseISO(slot.slot_start),
          slot_end: parseISO(slot.slot_end),
        }));

        setAvailabilityData(prev => ({
          ...prev,
          possible_slots: slots
        }));

        // Set initial date properly
        let initialDate = new Date();
        if (slots.length > 0) {
          if (mode === 'edit' && prefillDate) {
            initialDate = new Date(prefillDate);
          } else {
            initialDate = slots[0].slot_start;
          }
        }
        setSelectedDate(initialDate);
        setCurrentMonth(initialDate);

        // Pre-select the current booked slot in edit mode
        if (mode === 'edit' && prefillDate && prefillTime) {
          const prefilledSlot = slots.find(slot =>
            isSameDay(slot.slot_start, new Date(prefillDate)) &&
            format(slot.slot_start, 'HH:mm') === prefillTime
          );

          if (prefilledSlot) {
            setSelectedSlot(prefilledSlot);
            setSelectedDate(prefilledSlot.slot_start);
            setSelectedBatchId(prefilledSlot.batch_id);
            setCurrentMonth(prefilledSlot.slot_start);
          }
        }

      } catch (err) {
        console.error('Error fetching availability:', err);
        setError(err.message || 'Failed to load slot availability');
      } finally {
        setLoading(false);
      }
    };

    fetchAvailability();
  }, [assignId, mode, prefillDate, prefillTime]);

  const handleDateClick = (date) => {
    if (isDateAvailable(date)) {
      setSelectedDate(date);
      setSelectedSlot(null);
      setSelectedBatchId(null);
    }
  };

  const handleSlotClick = (slot) => {
    if (!slot.can_book) return;
    if (selectedSlot && selectedSlot.slot_start === slot.slot_start) {
      // Deselect if clicking same slot
      setSelectedSlot(null);
      setSelectedBatchId(null);
    } else {
      setSelectedSlot(slot);
      setSelectedBatchId(slot.batch_id);
    }
  };

  const openConfirmModal = () => {
    if (!selectedSlot) return;
    setBookingError(null); // Clear any previous error
    setShowConfirmModal(true);
  };

  const confirmBooking = async () => {
    if (!selectedSlot || isBooking) return;

    setIsBooking(true);
    setBookingError(null);

    try {
      let payload;
      let endpoint = '/coordinators/assign-exam/';
      let method = 'POST';

      console.log('Debug - Available exam IDs:', {
        examIdFromProps: examId,
        examIdFromDetails: examIdFromDetails,
        assignmentData: assignmentData,
        assignmentDataExamId: assignmentData?.exam_id,
        assignmentDataExamDetails: assignmentData?.examDetails
      });

      // Get exam ID - try multiple sources
      let examIdValue;

      // Priority 1: From props (passed from EmployeeSlotsTable)
      if (examId && examId !== "N/A") {
        examIdValue = examId;
      }
      // Priority 2: From separate fetch
      else if (examIdFromDetails) {
        examIdValue = examIdFromDetails;
      }
      // Priority 3: From assignmentData
      else if (assignmentData?.exam_id) {
        examIdValue = assignmentData.exam_id;
      }
      else if (assignmentData?.examDetails?.exam_id) {
        examIdValue = assignmentData.examDetails.exam_id;
      }
      else if (assignmentData?.examDetails?.id) {
        examIdValue = assignmentData.examDetails.id;
      }

      console.log('Final examIdValue:', examIdValue);

      if (!examIdValue) {
        throw new Error('Exam ID not found. Please contact support.');
      }

      // Convert to number
      examIdValue = parseInt(examIdValue);

      if (mode === 'edit' && examEmployeeId) {
        // UPDATE EXISTING EMPLOYEE SLOT - NO assignedData
        payload = {
          flag: 1,
          examEmployeeId: examEmployeeId,
          examData: [{
            examId: examIdValue
          }],
          assignedId: parseInt(assignId),
          scheduleDate: format(selectedSlot.slot_start, "yyyy-MM-dd'T'HH:mm:ss"),
          expiryDate: format(selectedSlot.slot_end, "yyyy-MM-dd'T'HH:mm:ss"),
          plantId: assignmentData?.plant_id || 1
        };
      } else {
        // Book new employee - WITH assignedData
        payload = {
          flag: 0,
          examData: [{
            examId: examIdValue
          }],
          assignedId: parseInt(assignId),
          scheduleDate: format(selectedSlot.slot_start, "yyyy-MM-dd'T'HH:mm:ss"),
          expiryDate: format(selectedSlot.slot_end, "yyyy-MM-dd'T'HH:mm:ss"),
          plantId: assignmentData?.plant_id || 1,
          assignedData: [{
            employeeName: employeeName,
            assignmentId: parseInt(assignId),
            employee_guid: employeeGuid,
          }]
        };
      }

      console.log('Sending payload:', payload);

      const response = await axios({
        url: endpoint,
        method,
        data: payload,
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true
      });

      const data = response.data;
      console.log('API Response:', data);

      if (response.status >= 200 && response.status < 300 && (data.message || data.success)) {
        setShowSuccessModal(true);
        setShowConfirmModal(false);
      } else {
        throw new Error(data.error || data.details || data.message || 'Failed to update slot');
      }
    } catch (err) {
      console.error("Booking error:", err);
      setBookingError(err.message || 'Failed to update slot. Please try again.');
    } finally {
      setIsBooking(false);
    }
  };

  const handleDone = () => {
    navigate('/hr/employee-slots', {
      state: {
        assignId,
        refresh: true,
        examId: examId || examIdFromDetails,
      },
      replace: true
    });
  };

  const isDateAvailable = (date) => {
    if (!availabilityData) return false;
    return availabilityData.possible_slots.some(slot =>
      isSameDay(slot.slot_start, date)
    );
  };

  const getSlotsForDate = (date) => {
    if (!availabilityData) return [];
    return availabilityData.possible_slots.filter(slot =>
      isSameDay(slot.slot_start, date)
    );
  };

  const getMonthDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    const days = [];

    // Start from Sunday of first week
    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(startDate.getDate() - startDate.getDay());

    // End on Saturday of last week
    const endDate = new Date(lastDayOfMonth);
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

    let current = new Date(startDate);
    while (current <= endDate) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  const goToPrevMonth = () => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const goToNextMonth = () => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="pd-table-loading">
        <div className="pd-table-loading-animation">
          <img
            className="pd-table-loading-video"
            src="/images/breath1.gif"
            alt="Loading slot availability..."
          />
          <p>Loading slot availability...</p>
        </div>
      </div>
    );
  }

  if (!availabilityData) {
    return (
      <div className="slot-booking-container">
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: '#6b7280' }}>No availability data found</p>
        </div>
      </div>
    );
  }

  const monthDays = getMonthDays();
  const slotsForSelectedDate = getSlotsForDate(selectedDate);
  const examDuration = assignmentData?.examDetails?.duration || 45;

  return (
    <Card>
      <div className="slot-booking-container">
        <div className="slot-booking-content">
          {/* Header with Employee Info */}
          <div className="slot-booking-header">
            <div className="slot-booking-header-top">
              <button
                type="button"
                className="pd-back-button"
                onClick={handleGoBack}
                aria-label="Go back"
              >
                <i className="fa fa-arrow-circle-left" aria-hidden="true"></i>
              </button>

              <h1 className="slot-booking-title">
                {mode === 'edit' ? 'Change Assessment Slot' : 'Book Assessment Slot'}
              </h1>
            </div>

            {employee && (
              <div className="slot-employee-info">
                <p className='change-assignment-type-btn mt-2'>
                  For: {employee.name}
                </p>
              </div>
            )}

            {assignmentData && (
              <div className="slot-exam-details">
                <p>
                  <strong>Assessment:</strong> {assignmentData.examDetails?.title || 'N/A'}
                </p>
                <p>
                  <strong>Duration:</strong> {examDuration} minutes
                </p>
              </div>
            )}
          </div>

          <div className="slot-booking-main-grid">
            {/* Calendar Section */}
            <div className="slot-calendar-section">
              <div className="slot-calendar-header">
                <h3 className="slot-calendar-month">
                  {format(currentMonth, 'MMMM yyyy')}
                </h3>
                <div className="slot-calendar-nav">
                  <button
                    className='btn-negative'
                    onClick={goToPrevMonth}
                  >
                    <ChevronLeft size={18} color="#6b7280" />
                  </button>
                  <button
                    className='btn-negative'
                    onClick={goToNextMonth}
                  >
                    <ChevronRight size={18} color="#6b7280" />
                  </button>
                </div>
              </div>

              <div className="slot-calendar-grid">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="slot-calendar-day-label">{day}</div>
                ))}

                {monthDays.map((day, idx) => {
                  const isAvailable = isDateAvailable(day);
                  const isSelected = isSameDay(day, selectedDate);

                  return (
                    <button
                      key={idx}
                      className={`slot-calendar-day ${isSelected ? 'slot-calendar-day-selected' : ''} ${!isAvailable ? 'slot-calendar-day-unavailable' : ''}`}
                      onClick={() => handleDateClick(day)}
                      disabled={!isAvailable}
                    >
                      {format(day, 'd')}
                    </button>
                  );
                })}
              </div>

              <div className="slot-selected-date-info">
                <Calendar size={18} color={PRIMARY} />
                <span><strong>Selected:</strong> {format(selectedDate, 'EEE, MMM d, yyyy')}</span>
              </div>
            </div>

            {/* Time Slots Section */}
            <div className="slot-times-section">
              <div className="slot-times-header">
                <Clock size={24} color={PRIMARY} />
                <h3>
                  Available Slots on {format(selectedDate, 'MMM d')}
                </h3>
              </div>

              <div className="slot-times-container">
                {slotsForSelectedDate.length === 0 ? (
                  <div className="slot-no-times">
                    <p>No slots available for this date</p>
                    <p className="slot-no-times-subtext">
                      Select another date from the calendar
                    </p>
                  </div>
                ) : (
                  <div className="slot-times-list">
                    {slotsForSelectedDate.map((slot, index) => {
                      const isSelected = selectedSlot && selectedSlot.slot_start === slot.slot_start;
                      const isBooked = slot.is_booked;
                      const canBook = slot.can_book;
                      const isFull = slot.remaining_capacity === 0;

                      let status, statusColor, statusBg;

                      if (isFull) {
                        status = 'No Slots Available';
                        statusColor = 'white';
                        statusBg = '#ef4444';
                      } else if (!canBook) {
                        status = 'Not Available';
                        statusColor = '#6b7280';
                        statusBg = '#f3f4f6';
                      } else {
                        status = 'Available';
                        statusColor = 'white';
                        statusBg = "#6ee7b7";
                      }

                      return (
                        <button
                          key={index}
                          className={`slot-time-item ${isSelected ? 'slot-time-item-selected' : ''} ${!canBook || isFull ? 'slot-time-item-disabled' : ''}`}
                          onClick={() => handleSlotClick(slot)}
                          disabled={!canBook || isFull}
                        >
                          <div className="slot-time-details">
                            <div className="slot-time-range">

                              {format(slot.slot_start, 'HH:mm')}

                            </div>
                          </div>

                          <div className="slot-time-status">
                            <span
                              className="slot-time-badge"
                              style={{ background: statusBg, color: statusColor }}
                            >
                              {status}
                            </span>
                            {/* 
                            {!canBook && !isFull && (
                              <Lock size={18} color="#92400e" />
                            )}

                            {canBook && !isFull && isSelected && (
                              <Check size={20} color={PRIMARY} />
                            )} */}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* <button
                className='btn slot-booking-button'
                onClick={openConfirmModal}
                disabled={
                  !selectedSlot ||
                  isBooking ||
                  selectedSlot.remaining_capacity === 0 ||
                  !selectedSlot.can_book
                }
              >
                {isBooking ? 'Processing...' : mode === 'edit' ? 'Update Slot' : 'Confirm Booking'}
              </button> */}
              {/* <div className="slot-booking-selected-info">
                {selectedSlot && (
                  <div className="slot-booking-selected-details">
                    <span className="selected-label">
                      Selected Time
                    </span>

                    <span className="selected-time">
                      {format(selectedSlot.slot_start, 'HH:mm')}
                    </span>
                  </div>
                )}
              </div>
              <button
                className="btn slot-booking-button"
                onClick={openConfirmModal}
                disabled={
                  !selectedSlot ||
                  isBooking ||
                  selectedSlot.remaining_capacity === 0 ||
                  !selectedSlot.can_book ||
                  (
                    mode === 'edit' &&
                    prefillDate &&
                    prefillTime &&
                    isSameDay(selectedSlot.slot_start, new Date(prefillDate)) &&
                    format(selectedSlot.slot_start, 'HH:mm') === prefillTime
                  )
                }
              >
                {isBooking
                  ? 'Processing...'
                  : mode === 'edit'
                    ? 'Update Slot'
                    : 'Confirm Booking'}
              </button> */}

              {/* <div className="slot-booking-footer">

                <div className="slot-booking-selected-info">
                  {selectedSlot ? (
                    <>
                      <div className="selected-icon">
                        ✓
                      </div>

                      <div className="selected-content">
                        <span className="selected-label">
                          Selected Slot
                        </span>

                        <span className="selected-time">
                          {format(selectedSlot.slot_start, 'HH:mm')}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="select-placeholder">
                      Select a time slot to continue
                    </div>
                  )}
                </div>


                <button
                  className="btn slot-booking-button"
                  onClick={openConfirmModal}
                  disabled={
                    !selectedSlot ||
                    isBooking ||
                    selectedSlot.remaining_capacity === 0 ||
                    !selectedSlot.can_book ||
                    (
                      mode === 'edit' &&
                      prefillDate &&
                      prefillTime &&
                      isSameDay(selectedSlot.slot_start, new Date(prefillDate)) &&
                      format(selectedSlot.slot_start, 'HH:mm') === prefillTime
                    )
                  }
                >
                  {isBooking
                    ? 'Processing...'
                    : mode === 'edit'
                      ? 'Update Slot'
                      : 'Confirm Booking'}
                </button>

              </div> */}

              <div className="slot-booking-footer">

                <div
                  className={`slot-booking-selected-info ${selectedSlot?.can_book ? "slot-available" : "slot-locked"
                    }`}
                >

                  {selectedSlot ? (
                    <>
                      <div className="selected-icon">
                        {selectedSlot.can_book ? "✓" : "🔒"}
                      </div>


                      <div className="selected-content">

                        <span className="selected-label">
                          {selectedSlot.can_book
                            ? "Selected Slot"
                            : "Slot Unavailable"}
                        </span>


                        <span className="selected-time">

                          {selectedSlot.can_book
                            ? format(selectedSlot.slot_start, 'HH:mm')
                            : "Choose another time"}

                        </span>

                      </div>
                    </>
                  ) : (

                    <div className="select-placeholder">
                      Select a time slot to continue
                    </div>

                  )}

                </div>



                <button
                  className="btn slot-booking-button"
                  onClick={openConfirmModal}
                  disabled={
                    !selectedSlot ||
                    isBooking ||
                    selectedSlot.remaining_capacity === 0 ||
                    !selectedSlot.can_book
                  }
                >

                  {isBooking
                    ? "Processing..."
                    : mode === "edit"
                      ? "Update Slot"
                      : "Confirm Booking"}

                </button>


              </div>

            </div>
          </div>
        </div>

        {/* Confirmation Modal with Error Display */}
        {showConfirmModal && selectedSlot && (
          <>
            <div
              className="slot-modal-overlay"
              onClick={() => !isBooking && setShowConfirmModal(false)}
            />
            <div className="slot-modal slot-confirm-modal">
              <div className="slot-modal-icon">
                <div className="slot-modal-icon-circle" style={{ background: PRIMARY }}>
                  <Calendar size={36} />
                </div>
                <h3 className="slot-modal-title">
                  {mode === 'edit' ? 'Change Slot for' : 'Confirm Slot for'} {employee?.name}
                </h3>
              </div>

              {/* Error Message Display */}
              {bookingError && (
                <div className="slot-modal-error">
                  <AlertCircle size={18} color={ERROR} />
                  <span>{bookingError}</span>
                </div>
              )}

              <div className="slot-modal-content">
                <div className="slot-modal-date">
                  {format(selectedSlot.slot_start, 'EEEE, MMMM d, yyyy')}
                </div>
                <div className="slot-modal-time">
                  {/* {format(selectedSlot.slot_start, 'h:mm a')} – {format(selectedSlot.slot_end, 'h:mm a')} */}
                  {format(selectedSlot.slot_start, 'HH:mm')} – {format(selectedSlot.slot_end, 'HH:mm')}
                </div>
                <div className="slot-modal-duration">
                  Duration: {examDuration} minutes
                </div>
              </div>

              <div className="slot-modal-actions">
                <button
                  className='btn-negative'
                  onClick={() => {
                    setShowConfirmModal(false);
                    setBookingError(null);
                  }}
                  disabled={isBooking}
                >
                  Cancel
                </button>
                <button
                  className='btn'
                  onClick={confirmBooking}
                  disabled={isBooking}
                >
                  {isBooking ? 'Processing...' : mode === 'edit' ? 'Update' : 'Confirm'}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Success Modal */}
        {showSuccessModal && selectedSlot && (
          <>
            <div className="slot-modal-overlay" />
            <div className="slot-modal slot-success-modal">
              <div className="slot-success-icon">
                <div className="slot-success-icon-circle" style={{ background: SUCCESS }}>
                  <Check size={44} />
                </div>
                <h2 className="slot-success-title">
                  Slot {mode === 'edit' ? 'Changed' : 'Booked'} Successfully!
                </h2>
                <p className="slot-success-message">
                  {employee?.name || 'Employee'}'s assessment slot has been {mode === 'edit' ? 'updated' : 'confirmed'}.
                </p>
              </div>

              <div className="slot-success-content">
                <div className="slot-success-date">
                  {format(selectedSlot.slot_start, 'EEEE, MMMM d, yyyy')}
                </div>
                <div className="slot-success-time">
                  {/* {format(selectedSlot.slot_start, 'h:mm a')} – {format(selectedSlot.slot_end, 'h:mm a')} */}
                  {format(selectedSlot.slot_start, 'HH:mm')} – {format(selectedSlot.slot_end, 'HH:mm')}
                </div>
              </div>

              <button
                className='btn slot-success-button'
                onClick={handleDone}
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
